// @ts-check

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppBridge } from "@shopify/app-bridge-react";
import { Banner, BlockStack, Grid, InlineStack, Layout, Page } from "@shopify/polaris";

import { PricingCard } from "components/PricingCard/PricingCard";

import { useGlobalContext } from "context/GlobalContext";

import { useAppQuery } from "hooks/use-app-query";

export default function PricingPlansPage() {
  const shopify = useAppBridge();
  const navigate = useNavigate();
  const { store, isFetchingStore, isRefetchingStore, refetchStore } = useGlobalContext();

  const [error, setError] = useState("");
  const [selectPlanLoading, setSelectPlanLoading] = useState(false);

  useEffect(() => {
    return () => {
      setError("");
      setSelectPlanLoading(false);
    }
  }, []);

  const {
    data: pricingPlans,
    isFetching: isFetchingPricingPlans,
  } = useAppQuery({
    url: "/api/pricing-plans",
    extraQueryKeys: ["pricingPlans"],
    reactQueryOptions: {
      staleTime: 10 * 60 * 1000,
    },
  });

  const plans = pricingPlans?.plans || [];

  const handlePlanClick = (planKey) => {
    setSelectPlanLoading(true);
    setError("");

    return fetch(`/api/billing-url?plan=${planKey}`)
      .then((res) => res.json())
      .then(({ success, error, data }) => {
        if (!success) {
          throw new Error(error);
        }

        if (data.redirectRequired) {
          open(data.redirectUrl, "_top");
        } else {
          refetchStore();
          shopify.toast.show("Plan changed");
          setSelectPlanLoading(false);
        }
      })
      .catch((error) => {
        setError(error.message);
        setSelectPlanLoading(false);
      });
  }

  let planCards;
  if (isFetchingPricingPlans) {
    planCards = Array.from({ length: 4 }, (v, i) => (
      <PricingCard loading />
    ));
  } else {
    planCards = plans.map((plan) => {
      let frequency;
      if (plan.interval === "EVERY_30_DAYS") {
        frequency = "monthly";
      }

      const selected = store?.subscription?.plan_key === plan.key;
      const ctaText = typeof plan.trialDays === "number" && plan.trialDays > 0 ? `Start ${plan.trialDays}-day free trial` : "Select plan";

      return (
        <PricingCard
          key={plan.key}
          title={plan.name}
          price={plan.amount}
          frequency={frequency}
          features={plan.features}
          button={{
            content: selected ? "Selected" : ctaText,
            disabled: selected,
            loading: selectPlanLoading || isFetchingStore || isRefetchingStore,
            variant: "primary",
            onClick: () => {
              handlePlanClick(plan.key);
            },
          }}
        />
      )
    });
  }

  let errorToRender;
  if (error) {
    errorToRender = (
      <Banner
        title="Error"
        tone="critical"
        onDismiss={() => setError("")}
        action={{
          content: "Contact support",
          onAction: () => window.$crisp.push(["do", "chat:open"]),
        }}
      >
        {error}
      </Banner>
    );
  }

  return (
    <Page
      title="Settings"
      backAction={{
        content: "Back to Settings",
        onAction: () => {
          navigate("/settings");
        },
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {errorToRender}

            <Grid columns={{ xs: 1, sm: 2, md: 4, lg: 4 }}>
              {planCards}
            </Grid>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  )
}
